/**
 * Utilitarios de servidor para o HTML de artigos (`articles.content`, vindo
 * do admin/TinyMCE ou de conversao de PDF). Antes, a injecao de `id` nos
 * h2/h3 e a extracao dos itens do sumario ("Neste artigo") aconteciam num
 * useEffect no cliente, depois do dangerouslySetInnerHTML montar - o que
 * significa que a primeira resposta HTML do servidor (o que o Googlebot le
 * na primeira passada, sem executar JS) nunca tinha esses ids nem o
 * sumario. Fazendo a mesma transformacao aqui, em texto, antes de mandar
 * para o cliente, os ids e o sumario ja chegam prontos no HTML inicial -
 * mesmo algoritmo (mesmo `slugify`, mesma regra de numeracao para titulos
 * duplicados), so que operando na string em vez do DOM montado.
 */
import { slugify } from './slugify';
import type { TocItem } from '../components/ArtigoToc';

/** Conteudo veio de pdftohtml (paginas com coordenadas absolutas em largura fixa) em vez de HTML normal do editor - ver PdfHtmlContent em artigos/[slug]. */
export function isConteudoPdf(conteudo: string | null | undefined): boolean {
  return !!conteudo && conteudo.includes('-div"');
}

/**
 * Injeta `id="..."` em cada `<h2>`/`<h3>` do HTML (preservando atributos
 * existentes, embora o conteudo do admin normalmente nao tenha nenhum) e
 * devolve a lista de itens do sumario, na mesma ordem e com a mesma regra
 * de numeracao (`-1`, `-2`...) para titulos com o mesmo texto que o
 * useEffect cliente ja usava. Sumario fica vazio (`[]`) com menos de 2
 * titulos, igual ao comportamento anterior (ArtigoToc some sozinho nesse
 * caso, mas evitar computar evita trabalho a toa).
 */
export function injetarIdsEExtrairToc(html: string): { html: string; itens: TocItem[] } {
  const itens: TocItem[] = [];
  const usados = new Map<string, number>();

  const novoHtml = html.replace(
    /<(h2|h3)((?:\s+[^>]*)?)>([\s\S]*?)<\/\1>/g,
    (_match: string, tag: string, attrs: string, inner: string) => {
      const texto = inner.replace(/<[^>]+>/g, '').trim();
      const base = slugify(texto) || 'secao';
      const n = usados.get(base) || 0;
      usados.set(base, n + 1);
      const id = n === 0 ? base : `${base}-${n}`;
      itens.push({ id, texto, nivel: tag === 'h3' ? 3 : 2 });
      return `<${tag}${attrs} id="${id}">${inner}</${tag}>`;
    }
  );

  return { html: novoHtml, itens: itens.length < 2 ? [] : itens };
}
